/**
 * Website intake (piece 20): the one unauthenticated, internet-facing
 * endpoint. 🔨 It maps the six configured fields and nothing else, caps each
 * field and the whole payload, rate-limits per source address and globally,
 * flags rather than blocks on the cheap checks, and answers every refusal
 * the same way so that nothing is enumerated.
 */
import { escapeHtml } from '../controls/richText';

export const INTAKE_FIELDS = ['name', 'company', 'email', 'phone', 'message', 'address'] as const;
export type IntakeField = (typeof INTAKE_FIELDS)[number];

export const ORIGIN_ALLOWLIST_KEY = 'leadintake.originAllowlist';
export const FIELD_BYTE_CAP_KEY = 'leadintake.fieldByteCap';
export const PAYLOAD_BYTE_CAP_KEY = 'leadintake.payloadByteCap';
export const PER_IP_PER_HOUR_KEY = 'leadintake.perIpPerHour';
export const GLOBAL_PER_HOUR_KEY = 'leadintake.globalPerHour';
export const MINIMUM_FILL_SECONDS_KEY = 'leadintake.minimumFillSeconds';
export const DEDUP_WINDOW_HOURS_KEY = 'leadintake.dedupWindowHours';
export const CAPTCHA_SITE_KEY = 'leadintake.captchaSiteKey';
export const CAPTCHA_SECRET_KEY = 'leadintake.captchaSecret';
export const DISPATCH_DEPARTMENT_KEY = 'leadintake.dispatchDepartmentId';
export const RULE_RELEASE_RATE_KEY = 'leadintake.ruleReleaseRateThreshold';
export const CONVERSION_OVERRIDE_ATTEMPTS_KEY = 'leads.conversionOverrideAttempts';
/** The age in months at which a captured contact, and a texted card nobody answered, is cleared; default 24 (piece 20). */
export const CAPTURED_CONTACT_RETENTION_MONTHS_KEY = 'leadintake.capturedContactRetentionMonths';
export const HYGIENE_RUN_HOUR_KEY = 'hygiene.nightlyRunHour';
export const ABSENCE_GRACE_DAYS_KEY = 'followup.absenceGraceDays';
export const FOLLOWUP_THRESHOLD_KEYS = {
  customer: 'followup.customerDays',
  prospect: 'followup.prospectDays',
  vendorOnly: 'followup.vendorOnlyDays',
  other: 'followup.otherDays',
} as const;

/** The whole-payload cap a post is held to where no Settings row says otherwise: 16,000 bytes, settled 2026-09-14 (piece 20; decision 13051), room for six fields at their 2,000-byte cap. */
export const PAYLOAD_BYTE_CAP_DEFAULT = 16_000;

/** The Organization settings this piece seeds; the allowlist seeds empty and the dispatch department is set at cutover, so both are absent here. */
export const PIECE_20_SETTINGS: readonly { key: string; value: number | string[] }[] = [
  { key: ORIGIN_ALLOWLIST_KEY, value: [] },
  { key: FIELD_BYTE_CAP_KEY, value: 2000 },
  { key: PAYLOAD_BYTE_CAP_KEY, value: PAYLOAD_BYTE_CAP_DEFAULT },
  { key: PER_IP_PER_HOUR_KEY, value: 10 },
  { key: GLOBAL_PER_HOUR_KEY, value: 500 },
  { key: MINIMUM_FILL_SECONDS_KEY, value: 3 },
  { key: DEDUP_WINDOW_HOURS_KEY, value: 24 },
  { key: RULE_RELEASE_RATE_KEY, value: 50 },
  { key: CONVERSION_OVERRIDE_ATTEMPTS_KEY, value: 3 },
  { key: HYGIENE_RUN_HOUR_KEY, value: 2 },
  { key: ABSENCE_GRACE_DAYS_KEY, value: 7 },
  { key: FOLLOWUP_THRESHOLD_KEYS.customer, value: 60 },
  { key: FOLLOWUP_THRESHOLD_KEYS.prospect, value: 30 },
  { key: FOLLOWUP_THRESHOLD_KEYS.vendorOnly, value: 180 },
  { key: FOLLOWUP_THRESHOLD_KEYS.other, value: 90 },
];

export interface CapturedContact {
  name: string | null;
  email: string | null;
  phone: string | null;
  companyName: string | null;
  message: string | null;
  /** Decision 12990: street, city, state and postal code as the visitor typed them, one line each; a row written before it carries no key. */
  address?: string | null;
}

/** 🔨 Every refused post gets one answer: 400 with an empty JSON body; a rate-limited post 429 with the same body. */
export const INTAKE_REFUSED = { status: 400, body: {} } as const;
export const INTAKE_LIMITED = { status: 429, body: {} } as const;
export const INTAKE_ACCEPTED = { status: 200, body: {} } as const;

const bytes = (text: string) => new TextEncoder().encode(text).length;

/**
 * The six configured keys and no other; an unexpected key is ignored rather
 * than stored. A field over its cap, or a payload over its cap, refuses the
 * post. The honeypot is a sixth key the form renders hidden; a filled one is
 * a signal, never a refusal.
 */
export function mapIntakePayload(raw: unknown, caps: { fieldBytes: number; payloadBytes: number }): { contact: CapturedContact; honeypotFilled: boolean; fillSeconds: number | null } | null {
  if (!raw || typeof raw !== 'object') return null;
  const body = raw as Record<string, unknown>;
  const text = (key: string): string | null => {
    const value = body[key];
    if (value === undefined || value === null) return null;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  };
  const contact: CapturedContact = { name: text('name'), companyName: text('company'), email: text('email'), phone: text('phone'), message: text('message'), address: text('address') };
  let total = 0;
  for (const value of Object.values(contact)) {
    if (value === null) continue;
    const size = bytes(value);
    if (size > caps.fieldBytes) return null;
    total += size;
  }
  if (total > caps.payloadBytes) return null;
  const honeypot = body.website ?? body.url ?? body.fax;
  const honeypotFilled = typeof honeypot === 'string' && honeypot.trim() !== '';
  const started = typeof body.startedAt === 'number' ? body.startedAt : typeof body.startedAt === 'string' ? Number(body.startedAt) : null;
  const fillSeconds = started !== null && Number.isFinite(started) ? Math.max(0, (Date.now() - started) / 1000) : null;
  return { contact, honeypotFilled, fillSeconds };
}

/** Tenant resolution comes from the URL and the origin from the allowlist; an origin the list does not carry is refused before any layer runs. */
export function originAllowed(origin: string | null | undefined, allowlist: readonly string[]): boolean {
  if (!origin) return false;
  const normalised = origin.trim().toLowerCase().replace(/\/$/, '');
  return allowlist.some((entry) => entry.trim().toLowerCase().replace(/\/$/, '') === normalised);
}

/**
 * A sliding hour window per key; per-IP and global are two limiters, each independent.
 * 🔨 A key whose newest hit is a whole window old is dropped by a sweep that runs at most once a window, so an
 * open route met from ever-new addresses holds the keys of two windows at most and never every address it saw.
 */
export class HourlyLimiter {
  private readonly hits = new Map<string, number[]>();
  private sweptAt = Number.NEGATIVE_INFINITY;

  constructor(private readonly limit: number, private readonly windowMs = 3_600_000) {}

  /** How many keys the limiter holds now. */
  get trackedKeys(): number {
    return this.hits.size;
  }

  /** Records the hit and answers whether it is within the limit. */
  admit(key: string, now = Date.now()): boolean {
    if (now - this.sweptAt >= this.windowMs) this.sweep(now);
    const kept = (this.hits.get(key) ?? []).filter((at) => now - at < this.windowMs);
    if (kept.length >= this.limit) {
      this.hits.set(key, kept);
      return false;
    }
    kept.push(now);
    this.hits.set(key, kept);
    return true;
  }

  reset(): void {
    this.hits.clear();
    this.sweptAt = Number.NEGATIVE_INFINITY;
  }

  private sweep(now: number): void {
    for (const [key, times] of this.hits) {
      const newest = times[times.length - 1];
      if (newest === undefined || now - newest >= this.windowMs) this.hits.delete(key);
    }
    this.sweptAt = now;
  }
}

/** 🔨 Intake dedup is bounded by a window, never by all history: an open Lead with the same email inside the window absorbs the post. */
export function dedupMatches(existing: { email: string | null; status: string; createdAt: Date | string } | null, email: string | null, windowHours: number, now: Date): boolean {
  if (!existing || !email || !existing.email) return false;
  if (existing.email.trim().toLowerCase() !== email.trim().toLowerCase()) return false;
  if (!['New', 'Working', 'Held'].includes(existing.status)) return false;
  return now.getTime() - new Date(existing.createdAt).getTime() <= windowHours * 3_600_000;
}

/** The lead's own outcome from the screener's: a Block verdict writes Blocked, a Hold from the score writes Held, everything else New. */
export function statusFromOutcome(outcome: 'Passed' | 'Noted' | 'Held' | 'Blocked'): 'New' | 'Held' | 'Blocked' {
  return outcome === 'Blocked' ? 'Blocked' : outcome === 'Held' ? 'Held' : 'New';
}

/** The held-leads report: releases over holds per rule, and a rule releasing above the threshold surfaced for removal. */
export function releaseRates(rows: readonly { signalKey: string; held: number; released: number }[], thresholdPercent: number): { signalKey: string; held: number; released: number; ratePercent: number; surfaced: boolean }[] {
  return rows.map((row) => {
    const ratePercent = row.held === 0 ? 0 : Math.round((row.released / row.held) * 1000) / 10;
    return { ...row, ratePercent, surfaced: row.held > 0 && ratePercent > thresholdPercent };
  });
}

/** The site address a Lead's captured address becomes at conversion (decision 12990). */
export interface CapturedAddressParts {
  addressLines: string[];
  locality: string | null;
  administrativeArea: string | null;
  postalCode: string | null;
}

/**
 * 🔒 Parsed only here, at the moment a person presses Create new (decision 12990): four lines whose third is a
 * two-letter state and whose fourth is a five- or nine-digit US postal code become the street, the mailing city,
 * the state and the code. Any other shape stays whole, its lines joined by a comma and a space, in the street line.
 */
export function capturedAddressParts(text: string | null | undefined): CapturedAddressParts {
  const lines = (text ?? '').split(/\r?\n/u).map((line) => line.trim()).filter((line) => line !== '');
  if (lines.length === 4 && /^[A-Za-z]{2}$/u.test(lines[2]!) && /^\d{5}(-\d{4})?$/u.test(lines[3]!)) {
    return { addressLines: [lines[0]!], locality: lines[1]!, administrativeArea: lines[2]!.toUpperCase(), postalCode: lines[3]! };
  }
  return { addressLines: lines.length === 0 ? [] : [lines.join(', ')], locality: null, administrativeArea: null, postalCode: null };
}

/** Rendered text from a stranger is escaped at render and never interpolated into mail, a query or a filename; this is the one escape. */
export function escapeCaptured(text: string | null | undefined): string {
  return escapeHtml(text ?? '');
}
